import type { BuiltInTool, BuiltInToolError, JsonSchema } from './types'

/**
 * #1544: an enterprise's roles, for a person's own agent: the portal's role forms as one tool per `/v1/roles…` route,
 * each under the scope that route needs (`roles:read` to read, `roles:write` to change). The MCP server serves them
 * as they stand (#1011) and chat offers them too, asking before every write (#321 decisions 4 and 5).
 *
 * None lists `run` among its callers, so a task cannot declare one (`tool_not_run_callable`) and none is re-pointed
 * at a run-scoped endpoint: inside a run an org change stays a plan a person signs off (#217, #1571).
 */

const ROLE_NOT_FOUND: BuiltInToolError = {
    code: 'role_not_found',
    when: "`id` names no role in this enterprise, another enterprise's included.",
    handling: 'tool_error',
}

const ROLE_SLUG_TAKEN: BuiltInToolError = {
    code: 'role_slug_taken',
    when: 'Another role in this enterprise already has that slug. A role given no slug takes one made from its name, so a name already taken is refused this way too.',
    handling: 'tool_error',
}

const TASK_NOT_FOUND: BuiltInToolError = {
    code: 'task_not_found',
    when: '`taskId` names no task in this enterprise.',
    handling: 'tool_error',
}

const MEMBER_NOT_FOUND: BuiltInToolError = {
    code: 'member_not_found',
    when: '`memberId` names no member in this enterprise.',
    handling: 'tool_error',
}

const ROLE_ID: JsonSchema = { type: 'string', description: 'The role, by its id from os.list_roles.' }

/** The fields a role is set by, as `POST /v1/roles` and `PATCH /v1/roles/:id` take them. */
function roleFields(change: boolean): Record<string, JsonSchema> {
    return {
        name: {
            type: 'string',
            description: change
                ? 'The role’s new name, up to 200 characters.'
                : 'The role’s name, up to 200 characters.',
        },
        slug: {
            type: 'string',
            description: change
                ? 'The role’s new slug, unique in the enterprise. A task names its role by this slug.'
                : 'The role’s slug, unique in the enterprise. Made from the name when left out.',
        },
    }
}

export const OS_ROLE_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.list_roles',
        family: 'os.org',
        summary: "The enterprise's roles, by slug.",
        description:
            "List the enterprise's roles, ordered by slug: each one's id, name and slug. A role is a job across the " +
            'enterprise, held by members on any team; it belongs to no team. No arguments. Never another ' +
            "enterprise's roles.",
        inputSchema: { type: 'object', properties: {}, additionalProperties: false },
        returns: 'One row per role, each with `id`, `name` and `slug`.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [],
        api: { method: 'GET', path: '/v1/roles', scope: 'roles:read' },
        examples: [{ input: {} }],
        public: true,
        chatPrompt: "os.list_roles lists the enterprise's roles.",
    },
    {
        name: 'os.read_role',
        family: 'os.org',
        summary: 'One role, with the members who hold it and the tasks that run as it.',
        description:
            'Read one role: its name and slug, the members who hold it (members), the teams those members sit on ' +
            '(teams, each with its holders), and the tasks that run ' +
            'as it (tasks). Not found for a role in another enterprise.',
        inputSchema: {
            type: 'object',
            properties: { id: ROLE_ID },
            required: ['id'],
            additionalProperties: false,
        },
        returns:
            'The role: `id`, `name`, `slug`, `members` (who holds it), `teams` (where it is held, each with its holders) and `tasks` (what runs as it).',
        callers: ['chat', 'api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [ROLE_NOT_FOUND],
        api: { method: 'GET', path: '/v1/roles/:id', scope: 'roles:read' },
        examples: [{ input: { id: '5f0c1d2e-0000-4000-8000-000000000011' } }],
        public: true,
        chatPrompt: 'os.read_role reads one role, with who holds it and what runs as it.',
    },
    {
        name: 'os.create_role',
        family: 'os.org',
        summary: 'Create a role.',
        description:
            'Create a role with a name and, optionally, a slug. A slug left out is made from the name. A slug, or a ' +
            'name with no slug, another role already has is refused with role_slug_taken. A role belongs to no team: ' +
            'it is held by members, every holder runs the role’s tasks on whatever team they sit, and a team runs the ' +
            'tasks of the roles its members hold. The response is the role as created.',
        inputSchema: {
            type: 'object',
            properties: roleFields(false),
            required: ['name'],
            additionalProperties: false,
        },
        returns: 'The role as created: `id`, `name`, `slug`, `members`, `teams` and `tasks`.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'write',
        retry: 'unsafe',
        retryNote:
            'Every call creates a role. A repeat with the same slug is refused (role_slug_taken) rather than creating a second.',
        errors: [ROLE_SLUG_TAKEN],
        api: { method: 'POST', path: '/v1/roles', scope: 'roles:write' },
        examples: [{ input: { name: 'Support lead' } }],
        public: true,
        chatPrompt: 'os.create_role creates a role. It asks the person first.',
    },
    {
        name: 'os.update_role',
        family: 'os.org',
        summary: "Change a role's name or slug.",
        description:
            "Change a role's name or slug; a field left out keeps its value. A slug another role already has is " +
            'refused with role_slug_taken. The response is the role after the change.',
        inputSchema: {
            type: 'object',
            properties: { id: ROLE_ID, ...roleFields(true) },
            required: ['id'],
            additionalProperties: false,
        },
        returns: 'The role after the change: `id`, `name`, `slug`, `members`, `teams` and `tasks`.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'The same fields set twice leave the role as the first call did.',
        errors: [ROLE_NOT_FOUND, ROLE_SLUG_TAKEN],
        api: { method: 'PATCH', path: '/v1/roles/:id', scope: 'roles:write' },
        examples: [{ input: { id: '5f0c1d2e-0000-4000-8000-000000000011', name: 'Support head' } }],
        public: true,
        chatPrompt: "os.update_role changes a role's name or slug. It asks the person first.",
    },
    {
        name: 'os.delete_role',
        family: 'os.org',
        summary: 'Remove a role nobody holds and no task runs as.',
        description:
            'Remove a role. A role a member still holds, or a task still runs as, is refused with role_has_dependents, ' +
            'naming each: take them off it first. The call is the confirmation, so the tool takes no confirm argument.',
        inputSchema: {
            type: 'object',
            properties: { id: ROLE_ID },
            required: ['id'],
            additionalProperties: false,
        },
        returns: 'Nothing: the role is gone.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'A repeat finds the role already gone and is refused with role_not_found, removing nothing else.',
        errors: [
            ROLE_NOT_FOUND,
            {
                code: 'role_has_dependents',
                when: 'A member still holds the role or a task still runs as it. The refusal names each.',
                handling: 'tool_error',
            },
        ],
        // `confirm=true` is the portal's guard against a stray click; over MCP and chat the call is the intent (#1544).
        api: { method: 'DELETE', path: '/v1/roles/:id?confirm=true', scope: 'roles:write' },
        examples: [{ input: { id: '5f0c1d2e-0000-4000-8000-000000000011' } }],
        public: true,
        chatPrompt: 'os.delete_role removes a role nobody holds. It asks the person first.',
    },
    {
        name: 'os.add_role_task',
        family: 'os.org',
        summary: 'Give a role a task: the task runs as that role.',
        description:
            'Give a role a task: the task runs as this role from now on, in place of any role it ran as before. The ' +
            'response is the role after the change.',
        inputSchema: {
            type: 'object',
            properties: { id: ROLE_ID, taskId: { type: 'string', description: 'The task that runs as the role.' } },
            required: ['id', 'taskId'],
            additionalProperties: false,
        },
        returns: 'The role after the change, with the task among its `tasks`.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'A task already on the role stays on it once.',
        errors: [ROLE_NOT_FOUND, TASK_NOT_FOUND],
        api: { method: 'POST', path: '/v1/roles/:id/tasks', scope: 'roles:write' },
        examples: [
            {
                input: {
                    id: '5f0c1d2e-0000-4000-8000-000000000011',
                    taskId: '5f0c1d2e-0000-4000-8000-000000000013',
                },
            },
        ],
        public: true,
        chatPrompt: 'os.add_role_task gives a role a task. It asks the person first.',
    },
    {
        name: 'os.remove_role_task',
        family: 'os.org',
        summary: 'Take a task away from a role.',
        description:
            'Take a task away from a role: the task runs as no role until it is given one. A task that does not run as ' +
            'this role is refused with task_not_on_role. The response is the role after the change.',
        inputSchema: {
            type: 'object',
            properties: { id: ROLE_ID, taskId: { type: 'string', description: 'The task to take off the role.' } },
            required: ['id', 'taskId'],
            additionalProperties: false,
        },
        returns: 'The role after the change, without the task among its `tasks`.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote:
            'A repeat finds the task already off the role and is refused with task_not_on_role, changing nothing.',
        errors: [
            ROLE_NOT_FOUND,
            TASK_NOT_FOUND,
            {
                code: 'task_not_on_role',
                when: 'The task does not run as this role.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'DELETE', path: '/v1/roles/:id/tasks/:taskId', scope: 'roles:write' },
        examples: [
            {
                input: {
                    id: '5f0c1d2e-0000-4000-8000-000000000011',
                    taskId: '5f0c1d2e-0000-4000-8000-000000000013',
                },
            },
        ],
        public: true,
        chatPrompt: 'os.remove_role_task takes a task away from a role. It asks the person first.',
    },
    {
        name: 'os.add_role_member',
        family: 'os.org',
        summary: 'Give a role a member: the member holds it.',
        description:
            'Give a role a member: the member holds the role from now on. The response is the role after the change.',
        inputSchema: {
            type: 'object',
            properties: { id: ROLE_ID, memberId: { type: 'string', description: 'The member who holds the role.' } },
            required: ['id', 'memberId'],
            additionalProperties: false,
        },
        returns: 'The role after the change, with the member among its `members`.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'A member who already holds the role holds it once.',
        errors: [ROLE_NOT_FOUND, MEMBER_NOT_FOUND],
        api: { method: 'POST', path: '/v1/roles/:id/members', scope: 'roles:write' },
        examples: [
            {
                input: {
                    id: '5f0c1d2e-0000-4000-8000-000000000011',
                    memberId: '5f0c1d2e-0000-4000-8000-000000000014',
                },
            },
        ],
        public: true,
        chatPrompt: 'os.add_role_member gives a role a member. It asks the person first.',
    },
    {
        name: 'os.remove_role_member',
        family: 'os.org',
        summary: 'Take a member away from a role.',
        description:
            'Take a member away from a role: the member no longer holds it. The response is the role after the change.',
        inputSchema: {
            type: 'object',
            properties: {
                id: ROLE_ID,
                memberId: { type: 'string', description: 'The member to take off the role.' },
            },
            required: ['id', 'memberId'],
            additionalProperties: false,
        },
        returns: 'The role after the change, without the member among its `members`.',
        callers: ['chat', 'api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'A member who no longer holds the role stays off it.',
        errors: [ROLE_NOT_FOUND, MEMBER_NOT_FOUND],
        api: { method: 'DELETE', path: '/v1/roles/:id/members/:memberId', scope: 'roles:write' },
        examples: [
            {
                input: {
                    id: '5f0c1d2e-0000-4000-8000-000000000011',
                    memberId: '5f0c1d2e-0000-4000-8000-000000000014',
                },
            },
        ],
        public: true,
        chatPrompt: 'os.remove_role_member takes a member away from a role. It asks the person first.',
    },
]
